import React, { useState, useEffect, useCallback } from "react"
import {
  TAgetUserFollowers,
  TAgetUserProfile,
  TAgetScannedProfiles,
} from "../../../services/userAPI"
import StickyDataTable from "../../../components/StickyDataTable"

interface Influencer {
  _id: string
  last_scrape: Date
  id: string
  access_token: string
  username: string
  full_name: string
  biography: string
  profile_pic: string
  from_hashtag_search: string
  brand_hashtag_counter: number
  next_process: Date
  followers: number
  following: number
  post_number: number
  average_like: number
  engagement_rate: number
  shared_posts: Record<string, unknown>[]
  cooperation_posts: {
    media_url: string
    like_count: number
    comments_count: number
    caption: string
  }[]
  keywords: string[]
  last_action: Date
  pricing: {
    price_tr: number
    price_us: number
    barter_price_tr: number
    barter_price_us: number
    price_post_tr: number
    price_post_us: number
    barter_price_post_tr: number
    barter_price_post_us: number
    price_reel_tr: number
    price_reel_us: number
    barter_price_reel_tr: number
    barter_price_reel_us: number
  }
  collaboration_count?: number
  failed_scrape: boolean
  public_email?: string
  contact_phone_number?: string
  mail_sent: boolean
  mail_sent_at: Date
  has_external_url: boolean
  has_profile_photo: boolean
  username_digit_ratio: number
  fullname_word_count: number
  fullname_digit_ratio: number
  name_equals_username: boolean
  bio_length: number
  is_private: boolean
  is_verified: boolean
  bio_links: Record<string, unknown>
  createdAt: string
  updatedAt: string
  __v: number
}

interface Relation {
  _id: string
  user: string | null
  influencer: string | null
  from_type: "follower" | "following" | "liked" | "commented"
  from_username: string
  from_insta_id: string
  to: string | null
  insta_id: string
  insta_username: string
  insta_fullname: string
  profile_pic: string
  is_private: boolean
  is_verified: boolean
  createdAt: string
  updatedAt: string
  __v: number
}

interface ScannedProfile {
  _id: string
  from_username: string
  from_type: "follower" | "following" | "liked" | "commented"
  from_insta_id: string
  updatedAt: string
  influencer: string
  influencer_data: Influencer
  relation: Relation
}

const BotAccountScanning = () => {
  const PAGE_SIZES = [10, 20, 30, 50, 100, 500, 1000]

  const [username, setUsername] = useState("")
  const [fromType, setFromType] = useState<"follower" | "following" | "liked" | "commented" | "">(
    "follower",
  )
  const [isScanning, setIsScanning] = useState(false)
  const [scanProgress, setScanProgress] = useState("")
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [scannedProfiles, setScannedProfiles] = useState<ScannedProfile[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const [filter, setFilter] = useState({
    username: "",
    fromType: "",
    page: 1,
    perPage: PAGE_SIZES[2],
    sortBy: "",
    sortOrder: false,
    csvExport: false,
  })

  const loadScannedProfiles = useCallback(async () => {
    try {
      setError("") // Clear any previous errors
      const response = await TAgetScannedProfiles(
        filter.username,
        filter.fromType,
        filter.page,
        filter.perPage,
        filter.sortBy,
        filter.sortOrder,
        false,
      )
      if (response && response.data) {
        setScannedProfiles(response.data)
        setTotalRecords(response.totalCount?.[0]?.count || 0)
      }
    } catch (error) {
      setError("Taranmış profiller yüklenirken hata oluştu")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  useEffect(() => {
    loadScannedProfiles()
  }, [loadScannedProfiles])

  const handleSorting = (columnAccessor: string) => {
    if (columnAccessor === filter.sortBy) {
      setFilter((prev) => ({
        ...prev,
        sortOrder: prev.sortOrder ? false : true,
      }))
    } else {
      setFilter((prev) => ({
        ...prev,
        sortBy: columnAccessor,
      }))
      setFilter((prev) => ({
        ...prev,
        sortOrder: true,
      }))
    }
  }

  const handleFilterChange = (field: string, value: string) => {
    setFilter((prev) => ({
      ...prev,
      [field]: value,
      page: 1, // Reset to first page when filter changes
    }))
  }

  const clearFilters = () => {
    setFilter({
      username: "",
      fromType: "",
      page: 1,
      perPage: PAGE_SIZES[2],
      sortBy: "",
      sortOrder: false,
      csvExport: false,
    })
  }

  const hasActiveFilters = filter.username || filter.fromType

  const convertToCSV = (data: ScannedProfile[]) => {
    const headers = [
      "Kullanıcı Adı",
      "Tam İsim",
      "Hangi Hesaptan Tarandı",
      "Tür",
      "Takipçi Sayısı",
      "Takip Edilen Sayısı",
      "Gönderi Sayısı",
      "Profil Fotoğrafı",
      "Kullanıcı Adı Rakam Oranı (%)",
      "İsim Kelime Sayısı",
      "İsim Rakam Oranı (%)",
      "İsim = Kullanıcı Adı",
      "Bio Uzunluğu",
      "Profil Gizliliği",
      "Hesap Doğrulaması",
      "Harici URL",
      "Tarama Tarihi",
    ]

    const rows = data.map((record) => [
      record.influencer_data.username,
      record.influencer_data.full_name,
      record.influencer_data.followers,
      record.influencer_data.following,
      record.influencer_data.post_number,
      record.influencer_data.has_profile_photo ? "Evet" : "Hayır",
      Math.round(record.influencer_data.username_digit_ratio * 100),
      record.influencer_data.fullname_word_count,
      Math.round(record.influencer_data.fullname_digit_ratio * 100),
      record.influencer_data.name_equals_username ? "Evet" : "Hayır",
      record.influencer_data.bio_length,
      record.influencer_data.is_private ? "Gizli" : "Herkese Açık",
      record.influencer_data.is_verified ? "Doğrulanmış" : "Doğrulanmamış",
      record.influencer_data.has_external_url ? "Var" : "Yok",
      formatDate(record.updatedAt),
    ])

    const csvContent = [headers, ...rows]
      .map((row) => row.map((item) => `"${item}"`).join(","))
      .join("\n")

    return csvContent
  }

  const downloadCSV = async () => {
    setIsExporting(true)
    setError("")
    setSuccess("")

    try {
      const response = await TAgetScannedProfiles("", "", 1, 1, "", false, true)

      if (!response || !Array.isArray(response.data) || response.data.length === 0) {
        setError("Dışa aktarılacak veri bulunmuyor")
        return
      }

      const csvString = convertToCSV(response.data)
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" })
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement("a")
      link.href = url
      link.setAttribute(
        "download",
        `bot-account-scanning-${new Date().toISOString().split("T")[0]}.csv`,
      )
      link.style.display = "none"
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      setSuccess("CSV dosyası başarıyla indirildi!")
    } catch (error) {
      setError("CSV dosyası indirilirken hata oluştu")
    } finally {
      setIsExporting(false)
    }
  }

  const handleScan = async () => {
    if (!username.trim()) {
      setError("Lütfen bir kullanıcı adı girin")
      return
    }

    setIsScanning(true)
    setError("")
    setSuccess("")
    setScanProgress("")

    try {
      // Step 1: Get user followers
      setScanProgress("Takipçiler alınıyor...")
      const followersResponse = await TAgetUserFollowers(username)

      if (followersResponse) {
        setScanProgress(`${fromType ? fromType + " " : ""}profil analiz ediliyor...`)

        // Step 2: Get user profile
        const profileResponse = await TAgetUserProfile(username, fromType)

        if (profileResponse) {
          setSuccess(
            `✅ ${username} hesabı ${
              fromType ? fromType + " türünde " : ""
            }başarıyla taranıp analiz edildi!`,
          )
          setScanProgress("")

          // Refresh the scanned profiles list
          loadScannedProfiles()
        }
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Bilinmeyen hata"
      setError(`Tarama hatası: ${errorMessage}`)
      setScanProgress("")
    } finally {
      setIsScanning(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isScanning) {
      handleScan()
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("tr-TR")
  }

  const getBooleanBadge = (value: boolean, trueText = "Evet", falseText = "Hayır") => {
    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${
          value ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
        }`}
      >
        {value ? trueText : falseText}
      </span>
    )
  }

  const getRatioBadge = (ratio: number) => {
    const percentage = Math.round(ratio * 100)
    const colorClass =
      percentage > 50
        ? "bg-red-100 text-red-800"
        : percentage > 25
        ? "bg-yellow-100 text-yellow-800"
        : "bg-green-100 text-green-800"

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colorClass}`}>
        %{percentage}
      </span>
    )
  }

  const columns = [
    {
      accessor: "influencer_data.username",
      title: "Kullanıcı",
      sortable: true,
      width: "300px",
      sortOrder: filter.sortBy === "influencer_data.username" ? filter.sortOrder : undefined,
      sortBy: filter.sortBy === "influencer_data.username",
      render: (record: ScannedProfile) => (
        <div className="flex items-center min-w-0">
          {record.influencer_data.profile_pic && (
            <img
              className="h-8 w-8 rounded-full mr-3 flex-shrink-0"
              src={record.influencer_data?.profile_pic}
              alt={record.influencer_data?.username}
            />
          )}
          <div className="min-w-0 flex-1">
            <a
              className="text-sm font-medium text-gray-900 truncate"
              href={`https://www.instagram.com/${record.influencer_data.username}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              @{record.influencer_data.username}
            </a>
            <div className="text-sm text-gray-500 truncate">{record.influencer_data.full_name}</div>
          </div>
        </div>
      ),
    },
    {
      accessor: "from_username",
      title: "Hangi Hesaptan Tarandı",
      sortable: true,
      width: "120px",
      sortOrder: filter.sortBy === "from_username" ? filter.sortOrder : undefined,
      sortBy: filter.sortBy === "from_username",
      render: (record: ScannedProfile) => (
        <div className="text-sm text-gray-900 text-center">{record.from_username}</div>
      ),
    },
    {
      accessor: "from_type",
      title: "Tür",
      sortable: true,
      width: "120px",
      sortOrder: filter.sortBy === "from_type" ? filter.sortOrder : undefined,
      sortBy: filter.sortBy === "from_type",
      render: (record: ScannedProfile) => (
        <div className="text-sm text-gray-900 text-center">{record.from_type}</div>
      ),
    },
    {
      accessor: "influencer_data.followers",
      title: "Takipçi / Takip Edilen",
      sortable: true,
      width: "120px",
      sortOrder: filter.sortBy === "influencer_data.followers" ? filter.sortBy : undefined,
      sortBy: filter.sortBy === "influencer_data.followers",
      render: (record: ScannedProfile) => (
        <div className="text-sm text-gray-900 text-center">
          {record.influencer_data.followers.toLocaleString()} /{" "}
          {record.influencer_data.following.toLocaleString()}
        </div>
      ),
    },
    {
      accessor: "influencer_data.has_profile_photo",
      title: "Profil Fotoğrafı",
      sortable: true,
      width: "140px",
      sortOrder:
        filter.sortBy === "influencer_data.has_profile_photo" ? filter.sortOrder : undefined,
      sortBy: filter.sortBy === "influencer_data.has_profile_photo",
      render: (record: ScannedProfile) => getBooleanBadge(record.influencer_data.has_profile_photo),
    },
    {
      accessor: "influencer_data.post_number",
      title: "Gönderi Sayısı",
      sortable: true,
      width: "140px",
      sortOrder: filter.sortBy === "influencer_data.post_number" ? filter.sortOrder : undefined,
      sortBy: filter.sortBy === "influencer_data.post_number",
      render: (record: ScannedProfile) => (
        <div className="text-sm text-gray-900 text-center">
          {record.influencer_data.post_number.toLocaleString()}
        </div>
      ),
    },
    {
      accessor: "influencer_data.username_digit_ratio",
      title: "Kullanıcı Adı Rakam Oranı",
      sortable: true,
      width: "200px",
      sortOrder:
        filter.sortBy === "influencer_data.username_digit_ratio" ? filter.sortOrder : undefined,
      sortBy: filter.sortBy === "influencer_data.username_digit_ratio",
      render: (record: ScannedProfile) =>
        getRatioBadge(record.influencer_data.username_digit_ratio),
    },
    {
      accessor: "influencer_data.fullname_word_count",
      title: "İsim Kelime Sayısı",
      sortable: true,
      width: "150px",
      sortOrder:
        filter.sortBy === "influencer_data.fullname_word_count" ? filter.sortOrder : undefined,
      sortBy: filter.sortBy === "influencer_data.fullname_word_count",
      render: (record: ScannedProfile) => (
        <div className="text-sm text-gray-900 text-center">
          {record.influencer_data.fullname_word_count}
        </div>
      ),
    },
    {
      accessor: "influencer_data.fullname_digit_ratio",
      title: "İsim Rakam Oranı",
      sortable: true,
      width: "150px",
      sortOrder:
        filter.sortBy === "influencer_data.fullname_digit_ratio" ? filter.sortOrder : undefined,
      sortBy: filter.sortBy === "influencer_data.fullname_digit_ratio",
      render: (record: ScannedProfile) =>
        getRatioBadge(record.influencer_data.fullname_digit_ratio),
    },
    {
      accessor: "influencer_data.name_equals_username",
      title: "İsim = Kullanıcı Adı",
      sortable: true,
      width: "160px",
      sortOrder:
        filter.sortBy === "influencer_data.name_equals_username" ? filter.sortOrder : undefined,
      sortBy: filter.sortBy === "influencer_data.name_equals_username",
      render: (record: ScannedProfile) =>
        getBooleanBadge(record.influencer_data.name_equals_username),
    },
    {
      accessor: "influencer_data.bio_length",
      title: "Bio Uzunluğu",
      sortable: true,
      width: "120px",
      sortOrder: filter.sortBy === "influencer_data.bio_length" ? filter.sortOrder : undefined,
      sortBy: filter.sortBy === "influencer_data.bio_length",
      render: (record: ScannedProfile) => (
        <div className="text-sm text-gray-900 text-center">{record.influencer_data.bio_length}</div>
      ),
    },
    {
      accessor: "influencer_data.is_private",
      title: "Profil Gizliliği",
      sortable: true,
      width: "140px",
      sortOrder: filter.sortBy === "influencer_data.is_private" ? filter.sortOrder : undefined,
      sortBy: filter.sortBy === "influencer_data.is_private",
      render: (record: ScannedProfile) =>
        getBooleanBadge(!record.influencer_data.is_private, "Herkese Açık", "Gizli"),
    },
    {
      accessor: "influencer_data.is_verified",
      title: "Hesap Doğrulaması",
      sortable: true,
      width: "160px",
      sortOrder: filter.sortBy === "influencer_data.is_verified" ? filter.sortOrder : undefined,
      sortBy: filter.sortBy === "influencer_data.is_verified",
      render: (record: ScannedProfile) =>
        getBooleanBadge(record.influencer_data.is_verified, "Doğrulanmış", "Doğrulanmamış"),
    },
    {
      accessor: "influencer_data.has_external_url",
      title: "Harici URL",
      sortable: true,
      width: "120px",
      sortOrder:
        filter.sortBy === "influencer_data.has_external_url" ? filter.sortOrder : undefined,
      sortBy: filter.sortBy === "influencer_data.has_external_url",
      render: (record: ScannedProfile) =>
        getBooleanBadge(record.influencer_data.has_external_url, "Var", "Yok"),
    },
    {
      accessor: "updatedAt",
      title: "Tarama Tarihi",
      sortable: true,
      width: "150px",
      sortOrder: filter.sortBy === "updatedAt" ? filter.sortOrder : undefined,
      sortBy: filter.sortBy === "updatedAt",
      render: (record: ScannedProfile) => (
        <div className="text-sm text-gray-500">{formatDate(record.updatedAt)}</div>
      ),
    },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Bot Hesap Tarama</h1>
        <p className="text-gray-600">
          Sosyal medya hesaplarını bot hesaplar için tarayın ve analiz edin
        </p>
      </div>

      {/* Scanning Form */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Yeni Hesap Tara</h2>

        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Kullanıcı Adı</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Kullanıcı adını girin (örn: @username)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isScanning}
            />
          </div>
          <div className="w-48">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tarama Türü (Opsiyonel)
            </label>
            <select
              value={fromType}
              // onChange={(e) =>
              //   setFromType(e.target.value as "follower" | "following" | "liked" | "commented" | "")
              // }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isScanning}
            >
              <option value="">Tarama Türü Seçin</option>
              <option value="follower">Takipçiler</option>
              <option value="following">Takip Edilenler</option>
              <option value="liked">Beğenilenler</option>
              <option value="commented">Yorum Yapılanlar</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleScan}
              disabled={isScanning || !username.trim()}
              className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isScanning ? "Taranıyor..." : "Taramayı Başlat"}
            </button>
          </div>
        </div>

        {scanProgress && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
              <span className="text-blue-800">{scanProgress}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-red-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-green-800">{success}</span>
            </div>
          </div>
        )}
      </div>

      {/* Scanned Accounts Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Taranmış Hesaplar</h2>
            <p className="text-sm text-gray-600">Taranmış hesapları filtreleyin ve arayın</p>
          </div>

          {scannedProfiles.length > 0 && (
            <button
              onClick={downloadCSV}
              disabled={isExporting}
              className="px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed mt-4 lg:mt-0"
            >
              {isExporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  İndiriliyor...
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  CSV İndir
                </>
              )}
            </button>
          )}
        </div>

        {/* Filter Status */}
        {hasActiveFilters && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg
                  className="w-4 h-4 text-blue-600 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
                  />
                </svg>
                <span className="text-sm font-medium text-blue-800">Aktif Filtreler:</span>
              </div>
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Tümünü Temizle
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {filter.username && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Kullanıcı: {filter.username}
                </span>
              )}
              {filter.fromType && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Tür: {filter.fromType}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Filter Form */}
        <form
          className="bg-gray-50 rounded-lg p-4"
          onSubmit={(e) => {
            e.preventDefault()
            loadScannedProfiles()
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Kullanıcı Adı</label>
              <input
                type="text"
                value={filter.username}
                onChange={(e) => handleFilterChange("username", e.target.value)}
                placeholder="Kullanıcı adını girin"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                disabled={isScanning}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tarama Türü</label>
              <select
                value={filter.fromType}
                onChange={(e) => handleFilterChange("fromType", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                disabled={isScanning}
              >
                <option value="">Tüm Tarama Türleri</option>
                <option value="follower">Takipçiler</option>
                <option value="following">Takip Edilenler</option>
                <option value="liked">Beğenilenler</option>
                <option value="commented">Yorum Yapılanlar</option>
              </select>
            </div>

            <div className="flex items-end">
              <div className="flex gap-2 w-full">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                  disabled={isScanning}
                >
                  <svg
                    className="w-4 h-4 inline mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  Ara
                </button>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="px-4 py-2 bg-gray-500 text-white font-medium rounded-md hover:bg-gray-600 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-sm"
                    disabled={isScanning}
                    title="Filtreleri Temizle"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>

      <StickyDataTable
        records={scannedProfiles}
        columns={columns}
        totalRecords={totalRecords}
        recordsPerPage={filter.perPage}
        page={filter.page}
        onPageChange={(p) => setFilter({ ...filter, page: p })}
        recordsPerPageOptions={PAGE_SIZES}
        onRecordsPerPageChange={(size) => setFilter({ ...filter, perPage: size })}
        sortStatus={{ columnAccessor: filter.sortBy, direction: filter.sortOrder ? "desc" : "asc" }}
        onSortStatusChange={(e) => handleSorting(e.columnAccessor)}
        minHeight={200}
        paginationText={({ from, to, totalRecords }) =>
          `Showing ${from} to ${to} of ${totalRecords} entries`
        }
        highlightOnHover={true}
        className="whitespace-nowrap"
      />
    </div>
  )
}

export default BotAccountScanning
